import { goto } from "$app/navigation";
import { isAuthenticated, user } from './stores';
import moment from 'moment-timezone';
import { tick } from 'svelte';
import { setError } from '/stores/error';

export default myfetch;
let unauthorized;
export function setUnauthorizedHandler(handler) {
    unauthorized = handler;
}

async function myfetch(url, options = {}) {
    const token = localStorage.getItem('accessToken');
    const endpoint = `${options.method || 'GET'} ${url}`;

    const enhancedOptions = {
        ...options,
        headers: {
            ...options.headers,
            'AccessToken': `${token}`,
        },
    };

    let response;
    try {
        response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, enhancedOptions);
    } catch (err) {
        setError({ message: `Network error: ${err.message}`, stack: err.stack || '', source: 'api', endpoint });
        throw err;
    }

    if (response.status === 401 && unauthorized) {
        unauthorized();
    } else if (response.status >= 500) {
        setError({ message: `Server error ${response.status} on ${url}`, source: 'api', statusCode: response.status, endpoint });
    }

    return response;
}

const TIMEZONE = 'Asia/Seoul';
const FORMAT = 'YYYY-MM-DD HH:mm:ss';

export function focusOnShow(node, show = false) {
    async function focus() {
        await tick();
        node.focus();
    }

    if (show) focus();

    return {
        update(newShow) {
            if (newShow) focus();
        },
        destroy() {
            node.blur();
        }
    };
}

export function formatSeoulTime(dateString, format = FORMAT) {
    if (!dateString) {
        return '';
    }
    // dateString이 문자열인지 확인
    if (typeof dateString !== 'string') {
        return '';
    }
    if (dateString.includes('0001-01-01T00:00:00')) {
        return '';
    }
    return moment(dateString).tz(TIMEZONE).format(format);
}

// YYYY-MM-DD (Go zero time은 빈 문자열)
// 값이 없거나 파싱 불가하면 빈 문자열을 돌려준다. moment는 실패 시 'Invalid date'를 반환하는데,
// 그 값이 그대로 저장되면 서버·배치가 날짜로 파싱하지 못해 멈춘다(계약 종료일 사고).
// moment(undefined)는 현재시각으로 해석되므로 falsy 가드가 따로 필요하다.
export function formatSeoulDate(dateString) {
    if (!dateString) {
        return '';
    }
    const date = getSeoulDateString(dateString);
    return date === '0001-01-01' ? '' : date;
}

// input type="date" 바인딩용 KST 날짜 문자열 (YYYY-MM-DD)
export function getSeoulDateString(date = new Date()) {
    const parsed = moment(date).tz(TIMEZONE);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

export function sanitizeSerialNumber(value) {
    return String(value ?? '').replace(/[^A-Za-z0-9-]/g, '');
}

export const SERIAL_NUMBER_VALIDATION_MESSAGE =
    '시리얼번호는 영문, 숫자, 하이픈(-)만 입력할 수 있습니다.';

export function isValidSerialNumber(value) {
    return /^[A-Za-z0-9-]+$/.test(String(value ?? ''));
}

export function formatNumber(num) {
    if (num < 0) {
        return 0;
    }
    const formattedNumber = new Intl.NumberFormat().format(num);
    return formattedNumber;
}

// 부호를 보존해 천단위 콤마로 포맷한다. formatNumber는 음수를 0으로 클램프하므로,
// 수정세금계산서의 부(-) 발행처럼 음수 금액이 정상인 컬럼에서는 이 함수를 쓴다.
export function formatSignedNumber(num) {
    return new Intl.NumberFormat().format(num || 0);
}

export function downloadCsv(filenamePrefix, headers, rows) {
    const escape = (cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`;
    const content = [
        headers.join(","),
        ...rows.map((row) => row.map(escape).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const stamp = moment().tz(TIMEZONE).format("YYYYMMDD_HHmmss");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filenamePrefix}_${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export async function downloadXlsx(filenamePrefix, headers, rows) {
    const response = await myfetch('/spreadsheets/xlsx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headers, rows }),
    });
    if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.msg || '엑셀 파일을 다운로드하지 못했습니다.');
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filenamePrefix}_${moment().tz(TIMEZONE).format('YYYYMMDD_HHmmss')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// 인증 첨부 열람/다운로드용 공통 헬퍼.
// <a href>/<img src> 직접요청은 AccessToken 헤더가 실리지 않으므로(브라우저가 보냄),
// 인증·워크스페이스 검증이 걸린 첨부는 myfetch(전역 fetch 래퍼)로 blob을 받아 처리한다.
// endpoint는 VITE_API_BASE_URL 이후의 경로(예: '/contracts/files/<key>')를 넘긴다.
// 실패하면 { url: '', msg }를 돌려준다. msg는 서버가 준 한국어 사유(`{ code, msg }`)이며,
// 화면이 사용자에게 그대로 띄울 수 있게 전달한다(setError는 수집용 로깅만 한다).
async function fetchAttachmentBlobUrl(endpoint) {
    const resp = await myfetch(endpoint);
    if (!resp.ok) {
        let msg = '';
        try {
            msg = (await resp.json())?.msg || '';
        } catch (_) {}
        setError({
            message: msg || '첨부파일을 불러오지 못했습니다.',
            source: 'api',
            endpoint: `GET ${endpoint}`,
            statusCode: resp.status,
        });
        return { url: '', msg: msg || '첨부파일을 불러오지 못했습니다.' };
    }
    const blob = await resp.blob();
    return { url: URL.createObjectURL(blob), msg: '' };
}

// 첨부를 새 탭에서 연다(이미지·PDF 미리보기). objectURL은 잠시 후 해제한다.
// 팝업 차단을 피하려고 사용자 제스처 동기 구간에서 먼저 빈 탭을 연 뒤 blob 으로 채운다
// (await 이후 window.open 은 transient activation 이 풀려 차단됨).
// 반환값 { ok, msg }로 실패 사유를 알려준다(호출 측에서 알림을 띄울 수 있게).
export async function openAttachment(endpoint) {
    const tab = window.open('', '_blank');
    const { url, msg } = await fetchAttachmentBlobUrl(endpoint);
    if (!url) {
        if (tab) tab.close();
        return { ok: false, msg };
    }
    if (tab) {
        tab.location = url;
    } else {
        // 팝업이 차단된 경우 다운로드로 폴백
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return { ok: true, msg: '' };
}

// 첨부를 파일로 저장한다.
export async function downloadAttachment(endpoint, filename) {
    const { url, msg } = await fetchAttachmentBlobUrl(endpoint);
    if (!url) return { ok: false, msg };
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // 브라우저가 blob 을 다 읽기 전에 해제되지 않도록 잠시 후 revoke 한다.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return { ok: true, msg: '' };
}

let loggingOut = false;
export async function logout() {
    if (loggingOut) return;
    loggingOut = true;
    try {
        const { clearPushOnLogout } = await import('/common/pwa');
        await clearPushOnLogout();
    } catch { /* 인증 만료·연결 끊김도 로그아웃은 완료한다. */ }
    isAuthenticated.set(false);
    user.set({});
    location.replace('/');
}

export function filterEngineers(users) {
    return users.filter((e) => e.Type === "owner" || e.Type === "member" || e.Type === "manager");
}

// 공급가액을 직접 입력했을 때 부가세·합계를 계산한다.
// 부가세 적용 시 부가세 = round(공급가액 × 0.1), 합계 = 공급가액 + 부가세.
// 미적용 시 부가세 = 0, 합계 = 공급가액(공급가액 = 합계).
export function supplyToVatTotal(supply, applyVat) {
    const s = Math.max(0, Math.round(supply || 0));
    const vat = applyVat ? Math.round(s * 0.1) : 0;
    return { vat, total: s + vat };
}

// 합계금액을 직접 입력했을 때 공급가액·부가세를 역산한다.
// 부가세 적용 시 공급가액 = round(합계 / 1.1), 부가세 = 합계 - 공급가액 → 공급가액 + 부가세 = 합계가 정확히 유지된다.
// 미적용 시 공급가액 = 합계, 부가세 = 0. (예: 합계 10,000 적용 → 공급가액 9,091 / 부가세 909)
export function totalToSupplyVat(total, applyVat) {
    const t = Math.max(0, Math.round(total || 0));
    if (!applyVat) return { supply: t, vat: 0 };
    const supply = Math.round(t / 1.1);
    return { supply, vat: t - supply };
}

// "N분에 한 번만" 같은 간격 제한. 간격이 지났으면 지금 시각을 기록하고 true를 준다.
// sessionStorage를 못 쓰는 환경에서는 제한 없이 매번 true를 준다.
export function throttleGate(key, intervalMs) {
    try {
        const last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last < intervalMs) return false;
        sessionStorage.setItem(key, String(Date.now()));
        return true;
    } catch {
        return true;
    }
}

export function isMobileDevice() {
    return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    ) || window.innerWidth < 768;
}

export function redirectToMobile(path) {
    if (isMobileDevice()) {
        void goto(`/mobile${path}`);
    }
}
